import { n } from 'neverpanic'

import type { UnknownObject } from '@scalar/workspace-store/helpers/general'
import { getLegacyWysiwygVersion } from '@scalar-org/entities/managed-doc'
import {
  type Guide,
  type HeaderLink,
  type HeaderLinkGuide,
  type HeaderLinkReference,
  type Project,
  type Reference,
  type SidebarItem,
  SidebarType,
} from '@scalar-org/entities/project'
import { slugify } from '@scalar-org/helpers/url'

import { createError } from '@/errors'
import type { HeadLinks, HeadMeta, LoadableContent } from '@/schema/head'
import type { NavigationRecord } from '@/schema/navigation/groups'
import type { HeaderItem } from '@/schema/navigation/header'
import type {
  NavigationConfig,
  NavigationRoutes,
} from '@/schema/navigation/navigation'
import type { PageNavigationEntry } from '@/schema/navigation/pages'
import { type ScalarConfig, schemaCdn } from '@/schema/schema'
import { createUrlSlug } from '@/transformers/helpers'

/** Generate Scalar Config guide route items from WYSIWYG sidebar items */
function generateGuideItems({
  children,
  items,
}: {
  children: string[]
  items: Record<string, SidebarItem>
}) {
  const routes: NavigationRecord = {}

  const sidebarItems = children.map((uid) => items[uid])

  sidebarItems.forEach((item) => {
    if (!item.show) {
      return
    }

    const slug = createUrlSlug({ name: item.title })

    const childrenRoutes = generateGuideItems({
      children: item.children ?? [],
      items,
    })

    if (item.type === SidebarType.Folder) {
      routes[slug] = {
        type: 'group',
        title: item.title,
        mode: 'folder',
        open: item.defaultOpen,
        children: childrenRoutes,
      }
    }

    if (item.type === SidebarType.Page) {
      const pageEntry: PageNavigationEntry = {
        type: 'page',
        title: item.title,
        description: item.description,
        icon: item.icon?.src,
        filepath: `${item.yjsReference}.yjs`,
      }

      routes[slug] = item.children.length
        ? {
            type: 'group',
            mode: 'folder',
            title: item.title ?? 'Page group',
            open: item.defaultOpen,
            icon: item.icon?.src,
            page: pageEntry,
            children: childrenRoutes,
          }
        : pageEntry
    }

    if (item.type === SidebarType.Link) {
      routes[slug] = {
        type: 'link',
        title: item.title,
        url: item.link,
        icon: item.icon?.src,
      }
    }
  })

  return routes
}

/** Generate a Scalar Config header from WYSIWYG header items */
function generateGuideHeader({
  header,
  guides,
  invalidRefs,
  references,
  isGroup,
}: {
  header: HeaderLink[]
  guides: Record<string, Guide>
  invalidRefs: Record<string, string>
  references: Record<string, Reference>
  isGroup?: boolean
}) {
  const headerItems: HeaderItem[] = []

  header.forEach((item) => {
    if (!item.show) return

    if (item.type === 'group') {
      headerItems.push({
        type: 'group',
        title: item.title,
        children: generateGuideHeader({
          header: item.children,
          guides,
          invalidRefs,
          references,
          isGroup: true,
        }).filter((item) => item.type !== 'group'),
      })
    }

    if (item.type === 'guide') {
      const guide = guides[item.uid]

      headerItems.push({
        type: 'link',
        title: guide.title,
        to: createUrlSlug({ name: guide.title }),
      })
    }

    if (item.type === 'reference') {
      const reference = references[item.uid]

      if (item.uid in invalidRefs && !reference.managedDoc) {
        return
      }

      headerItems.push({
        type: 'link',
        title: reference.title,
        to: createUrlSlug({ name: reference.title }),
      })
    }

    if (item.type === 'link') {
      headerItems.push({
        type: 'link',
        title: item.title,
        to: item.link,
        style: item.isButton ? 'button' : 'text',
      })
    }

    if (item.type === 'spacer') {
      headerItems.push({
        type: 'spacer',
      })
    }
  })

  if (!isGroup && !headerItems.some((item) => item.type === 'spacer')) {
    headerItems.push({
      type: 'spacer',
    })
  }

  return headerItems
}

/** Extract ordered guide/reference UIDs from header items, recursing into groups */
function getOrderedHeaderRefs(header: HeaderLink[]) {
  const ordered: Array<HeaderLinkGuide | HeaderLinkReference> = []

  header.forEach((item) => {
    if (item.type === 'guide') {
      ordered.push(item)
    }

    if (item.type === 'reference') {
      ordered.push(item)
    }

    if (item.type === 'group') {
      ordered.push(...getOrderedHeaderRefs(item.children))
    }
  })

  return ordered
}

/** Add a guide entry to routes if visible */
function addGuideToRoutes(routes: NavigationRoutes, guide: Guide) {
  if (!guide.show) return
  routes[createUrlSlug({ name: guide.title })] = {
    title: guide.title,
    type: 'group',
    children: generateGuideItems(guide.sidebar),
  }
}

/** Add a reference entry to routes if valid */
function addReferenceToRoutes(
  routes: NavigationRoutes,
  reference: Reference,
  invalidRefs: Record<string, string>,
) {
  if (!reference.show) return

  const parseConfigResult = n.fromUnsafe(
    (): UnknownObject => JSON.parse(reference.config),
    (originalError) => createError('INVALID_REFERENCE_CONFIG', originalError),
  )

  const config = parseConfigResult.success ? parseConfigResult.data : {}

  if (reference.specPermalink) {
    routes[createUrlSlug({ name: reference.title })] = {
      type: 'openapi',
      title: reference.title,
      description: reference.description,
      url: reference.specPermalink,
      config,
      singlePage: true,
    }
    return
  }

  if (reference.managedDoc) {
    routes[createUrlSlug({ name: reference.title })] = {
      type: 'openapi',
      title: reference.title,
      description: reference.description,
      namespace: reference.managedDoc.namespace,
      slug: reference.managedDoc.slug,
      singlePage: true,
      config,
      version: getLegacyWysiwygVersion(reference.managedDoc.versions),
    }
    return
  }

  if (reference.uid in invalidRefs) return

  routes[createUrlSlug({ name: reference.title })] = {
    type: 'openapi',
    title: reference.title,
    description: reference.description,
    config,
    singlePage: true,
    filepath: `${reference.yjsReference}.yaml`,
  }
}

/** Create Scalar Config navigation routes from WYSIWYG guides and references */
function generateGuideRoutes({
  guides,
  references,
  invalidRefs,
  header,
}: {
  guides: Record<string, Guide>
  references: Record<string, Reference>
  invalidRefs: Record<string, string>
  header: HeaderLink[]
}) {
  const routes: NavigationRoutes = {}
  const addedGuides = new Set<string>()
  const addedReferences = new Set<string>()

  getOrderedHeaderRefs(header).forEach((item) => {
    if (item.type === 'guide' && item.uid in guides) {
      addGuideToRoutes(routes, guides[item.uid])
      addedGuides.add(item.uid)
    }

    if (item.type === 'reference' && item.uid in references) {
      addReferenceToRoutes(routes, references[item.uid], invalidRefs)
      addedReferences.add(item.uid)
    }
  })

  Object.entries(guides).forEach(([uid, guide]) => {
    if (!addedGuides.has(uid)) {
      addGuideToRoutes(routes, guide)
    }
  })

  Object.entries(references).forEach(([uid, reference]) => {
    if (!addedReferences.has(uid)) {
      addReferenceToRoutes(routes, reference, invalidRefs)
    }
  })

  return routes
}

/** Convert WYSIWYG project structure to Scalar Config V2 */
export const wysiwygProjectToV2 = n.safeFn(
  ({
    project,
    invalidRefs,
    theme,
  }: {
    project: Project
    invalidRefs: Record<string, string>
    theme?: string
  }) => {
    const meta: HeadMeta[] = []

    if (project.website.description) {
      meta.push({
        name: 'description',
        content: project.website.description,
      })
    }

    const links: HeadLinks[] = []

    if (project.website.favicon) {
      links.push({
        rel: 'icon',
        type: 'image/x-icon',
        href: project.website.favicon,
      })
    }

    if (project.website.ogImage) {
      meta.push({
        property: 'og:image',
        content: project.website.ogImage,
      })
    }

    const versions: Record<string, NavigationConfig> = {}

    const projectVersions = Object.values(project.versions)

    const activeVersion =
      project.versions[project.activeVersionId] ??
      projectVersions[projectVersions.length - 1]

    for (const version of projectVersions) {
      const identifier =
        version.uid === activeVersion.uid ? 'default' : slugify(version.name)

      versions[identifier] = {
        title: version.name,
        header: generateGuideHeader({
          header: version.header,
          guides: version.guides,
          references: version.references,
          invalidRefs,
        }),
        routes: generateGuideRoutes({
          guides: version.guides,
          references: version.references,
          invalidRefs,
          header: version.header,
        }),
      }
    }

    const legacyFooterPath = 'footer.html'
    const legacyFooterThemePath = 'footer-theme.css'
    const legacyThemePath = 'theme.css'
    const legacyHeadScriptPath = 'head.js'
    const legacyBodyScriptPath = 'body.js'

    const styles: LoadableContent[] = [
      {
        path: legacyFooterThemePath,
      },
    ]

    if (theme) {
      styles.push({ path: legacyThemePath })
    }

    const scripts: LoadableContent[] = []

    if (activeVersion.headScript) {
      scripts.push({
        path: legacyHeadScriptPath,
        tagPosition: 'head',
      })
    }

    if (activeVersion.bodyScript) {
      scripts.push({
        path: legacyBodyScriptPath,
        tagPosition: 'bodyOpen',
      })
    }

    const upgradedConfig: ScalarConfig = {
      $schema: schemaCdn,
      scalar: '2.0.0',
      info: {
        title: project.name ?? project.website.title,
        description: project.website.description,
        version: activeVersion.name,
      },
      siteConfig: {
        ...(project.website.customUrl && {
          customDomain: project.website.customUrl,
        }),
        subdomain: project.website.subdomainPrefix,
        subpath: project.website.subPath,
        footer: {
          filepath: legacyFooterPath,
          belowSidebar: activeVersion.footerBelowSidebar,
        },
        ...((project.logo.darkMode || project.logo.lightMode) && {
          logo: {
            lightMode: project.logo.lightMode || project.logo.darkMode,
            darkMode: project.logo.darkMode || project.logo.lightMode,
          },
        }),
        head: {
          title: project.website.title,
          links,
          meta,
          styles,
          scripts,
        },
        routing: {
          redirects: project.website.redirects,
          ...(project.website.routing.guidePathPattern && {
            guidePathPattern: project.website.routing.guidePathPattern,
          }),
          ...(project.website.routing.referencePathPattern && {
            referencePathPattern: project.website.routing.referencePathPattern,
          }),
        },
      },
      versions,
    }

    return {
      success: true,
      data: {
        upgradedConfig,
        legacyFooter: {
          path: legacyFooterPath,
          footerString: activeVersion.footer,
        },
        legacyFooterTheme: {
          path: legacyFooterThemePath,
          cssString: activeVersion.footerCss,
        },
        ...(theme && {
          legacyTheme: {
            path: legacyThemePath,
            cssString: theme,
          },
        }),
        ...(activeVersion.headScript && {
          legacyHeadScript: {
            path: legacyHeadScriptPath,
            scriptString: activeVersion.headScript,
          },
        }),
        ...(activeVersion.bodyScript && {
          legacyBodyScript: {
            path: legacyBodyScriptPath,
            scriptString: activeVersion.bodyScript,
          },
        }),
      },
    }
  },
  (originalError) =>
    createError('FAILED_TO_CONVERT_WYSIWYG_TO_V2', originalError),
)
